import Layout from "@/components/Layout";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calculator } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { ArticleJsonLd, FaqJsonLd } from "@/components/JsonLd";
import { getKnowledgeArticle, getRelatedKnowledge } from "@/data/knowledgeBase";

const BazaWiedzyArtykul = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getKnowledgeArticle(slug) : undefined;

  if (!article || !slug) {
    return (
      <Layout>
        <SEOHead
          title="Nie znaleziono artykułu | Baza wiedzy"
          description="Nie znaleziono tego artykułu w bazie wiedzy o naklejkach 3D."
          noindex
        />
        <section className="py-24 text-center">
          <p className="text-muted-foreground text-lg mb-4">Nie znaleziono tego artykułu.</p>
          <Link to="/baza-wiedzy" className="text-accent font-semibold hover:underline">
            ← Wróć do bazy wiedzy
          </Link>
        </section>
      </Layout>
    );
  }

  const related = getRelatedKnowledge(article);

  return (
    <Layout breadcrumbTitle={article.title}>
      <SEOHead
        title={`${article.metaTitle} | naklejki-3d.com.pl`}
        description={article.metaDescription}
        ogType="article"
        articleDate={article.date}
      />
      <ArticleJsonLd
        title={article.title}
        description={article.metaDescription}
        date={article.date}
        slug={article.slug}
        basePath="/baza-wiedzy"
      />
      {article.faqs.length > 0 && <FaqJsonLd faqs={article.faqs} />}

      <section className="gradient-navy py-16">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <span className="text-accent text-xs font-semibold uppercase tracking-wide">
            {article.category}
          </span>
          <h1 className="text-3xl lg:text-4xl font-bold text-primary-foreground mt-3 mb-4">
            {article.title}
          </h1>
          <p className="text-primary-foreground/60 text-sm">
            {new Date(article.date).toLocaleDateString("pl-PL", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="container mx-auto px-4 max-w-3xl">
          <Link
            to="/baza-wiedzy"
            className="text-accent font-semibold flex items-center gap-2 mb-8 hover:underline text-sm"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Wróć do bazy wiedzy
          </Link>

          <p className="text-lg text-foreground/90 leading-relaxed mb-10">{article.lead}</p>

          {article.sections.map((s) => (
            <div key={s.h2} className="mb-10">
              <h2 className="text-2xl font-bold text-foreground mb-4">{s.h2}</h2>
              {s.paragraphs?.map((p) => (
                <p key={p} className="text-muted-foreground leading-relaxed mb-4">
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="space-y-2 mt-3">
                  {s.bullets.map((b) => (
                    <li key={b} className="text-muted-foreground leading-relaxed flex gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" aria-hidden="true" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {article.faqs.length > 0 && (
            <div className="mt-14 pt-10 border-t border-border">
              <h2 className="text-2xl font-bold text-foreground mb-6">Najczęstsze pytania</h2>
              <div className="space-y-5">
                {article.faqs.map((f) => (
                  <div key={f.q} className="bg-card border border-border rounded-lg p-5">
                    <h3 className="text-foreground font-semibold mb-2">{f.q}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-14 pt-10 border-t border-border">
            <h2 className="text-2xl font-bold text-foreground mb-6">Sprawdź także</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {article.links.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="block p-5 bg-card border border-border rounded-lg hover:border-accent/40 transition-colors"
                >
                  <h3 className="text-foreground font-semibold mb-1 text-sm">{l.label}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{l.text}</p>
                </Link>
              ))}
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-bold text-foreground mb-5">Powiązane poradniki</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/baza-wiedzy/${r.slug}`}
                    className="block p-5 bg-secondary/50 border border-border rounded-lg hover:border-accent/40 transition-colors"
                  >
                    <h3 className="text-foreground font-semibold text-sm mb-2 leading-snug">{r.title}</h3>
                    <span className="text-accent text-xs font-medium flex items-center gap-1">
                      Czytaj więcej <ArrowRight className="w-3 h-3" aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-14 bg-secondary/50 border border-border rounded-xl p-7 text-center">
            <p className="text-foreground font-semibold mb-1">Potrzebujesz wyceny?</p>
            <p className="text-muted-foreground text-sm mb-5">
              Podaj wymiary, nakład i folię – cenę netto zobaczysz od razu.
            </p>
            <Link
              to="/kalkulator"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              <Calculator className="w-4 h-4" aria-hidden="true" /> Przejdź do kalkulatora
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BazaWiedzyArtykul;
